import {
  ChallengeTableChallenge,
  ChallengeTableProgramVersionItem,

} from "../../../api/types/dtos/challenge/ChallengeDto";
import { CellState } from "./ChallengesDetailedTable.cellContext";
import { Form, Question } from "../../../api/types/forms";

type SortedPvis = ChallengeTableProgramVersionItem[];

export interface ChallengesDetailedTableExportData {
  challenges: ChallengeTableChallenge[];
  programVersionItems: ChallengeTableProgramVersionItem[];
}

export const buildChallengesCsvContent = (
  data: ChallengesDetailedTableExportData,
  cellState: CellState,
  getSortedPvis: (
    programVersionItems: ChallengeTableProgramVersionItem[],
  ) => SortedPvis,
  isEndDatePreviousYear: (endDate: string | null | undefined) => boolean,
): string => {
  const { challenges, programVersionItems } = data;
  const sortedPvis = getSortedPvis(programVersionItems);

  const headers = [
    "Organization ID",
    "Cédula",
    "Finca",
    "Productor",
    "Grupos",
    "Región",
    "Ciudad",
    "Vereda",
    ...sortedPvis.map((pvi) => pvi.cpNumber || `pvi_${pvi.id}`),
  ];

  const csvRows = challenges.map((challenge) => {
    const row = [
      challenge.inspectedOrgId || "",
      challenge.inspectedUserId || "",
      challenge.inspectedOrgName || "",
      challenge.inspectedUserName || "",
      (challenge.inspectedOrgGroupsName || []).join(", "),
      challenge.inspectedOrgRegionName || "",
      challenge.inspectedOrgCityName || "",
      challenge.inspectedOrgVeredaName || "",
    ];

    sortedPvis.forEach((pvi) => {
      const cellKey = `${challenge.inspectedOrgId}_${pvi.id}`;
      const local = cellState[cellKey];
      const item = challenge.inspectionsProgramsVersionsItems.find(
        (inspItem) => inspItem.programVersionItemId === pvi.id,
      );

      let status = "";
      if (item) {
        const currentIsActive = local?.isActive ?? item.isActive;
        const currentIsApproved = local?.isApproved ?? item.isApproved;
        const currentEndDate = local?.endDate ?? item.endDate;

        if (!currentIsActive) {
          status = "P";
        } else {
          if (!currentIsApproved) {
            status = "E";
          } else {
            if (isEndDatePreviousYear(currentEndDate)) {
              status = "A";
            } else {
              status = "C";
            }
          }
        }
      }
      row.push(status);
    });
    return row
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",");
  });

  const csvContent = [
    headers.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
    ...csvRows,
  ].join("\n");

  return csvContent;
};

export const buildTemplatesCsvContent = (
  data: ChallengesDetailedTableExportData,
  getSortedPvis: (
    programVersionItems: ChallengeTableProgramVersionItem[],
  ) => SortedPvis,
): string => {
  const { programVersionItems } = data;
  const sortedPvis = getSortedPvis(programVersionItems);

  const headers = ["Field ID", "Description", "Answer Type", "CP Number"];
  const rows: string[][] = [];

  sortedPvis.forEach((pvi) => {
    if (pvi.measurementTemplate) {
      try {
        const formDef = JSON.parse(pvi.measurementTemplate) as {
          questions: any[];
        };
        if (formDef && Array.isArray(formDef.questions)) {
          formDef.questions.forEach((question: any) => {
            rows.push([
              question.id || "",
              question.text || "",
              question.type || "",
              pvi.cpNumber || "",
            ]);
          });
        }
      } catch (e) {
        console.error(
          `Failed to parse measurementTemplate for pvi ${pvi.id}`,
          e,
        );
      }
    }
  });

  const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map(escapeCsv).join(",")),
  ].join("\n");

  return csvContent;
};

export const buildNamesCsvContent = (
  data: ChallengesDetailedTableExportData,
  getSortedPvis: (
    programVersionItems: ChallengeTableProgramVersionItem[],
  ) => SortedPvis,
): string => {
  const { programVersionItems } = data;
  const sortedPvis = getSortedPvis(programVersionItems);

  const headers = ["CP Number", "Name"];

  const csvRows = sortedPvis.map((pvi) => {
    const row = [pvi.cpNumber || "", pvi.name || ""];
    return row
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",");
  });

  const csvContent = [
    headers.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
    ...csvRows,
  ].join("\n");

  return csvContent;
};

export const buildFullExportCsvContent = (
  data: ChallengesDetailedTableExportData,
): string => {
  const { challenges, programVersionItems } = data;

  const fixedHeaders = ["Organization ID", "Cédula", "Productor", "Finca"];
  const questionMap = new Map<string, Question>();
  const dynamicHeaders = new Set<string>();

  programVersionItems.forEach((pvi) => {
    if (pvi.measurementTemplate) {
      try {
        const formDef = JSON.parse(pvi.measurementTemplate) as Form;
        if (formDef && Array.isArray(formDef.questions)) {
          formDef.questions.forEach((q) => {
            questionMap.set(q.id, q);
          });
        }
      } catch (e) {
        console.error(
          `Error parsing measurementTemplate for PVI ${pvi.id}:`,
          e,
        );
      }
    }
  });

  questionMap.forEach((q) => {
    dynamicHeaders.add(q.id);
    if (
      q.display_logic?.condition_type === "for_each" &&
      q.display_logic.source_multiselect_id
    ) {
      const parentQuestion = questionMap.get(
        q.display_logic.source_multiselect_id,
      );
      if (parentQuestion && parentQuestion.options) {
        parentQuestion.options.forEach((option) => {
          const dynamicHeader = `${parentQuestion.id}:${option.value}:${q.id}`;
          dynamicHeaders.add(dynamicHeader);
        });
      }
    }
  });

  const sortedDynamicHeaders = Array.from(dynamicHeaders).sort();
  const finalHeaders = [...fixedHeaders, ...sortedDynamicHeaders];

  const csvRows = challenges.map((challenge) => {
    const row = new Map<string, string>();

    row.set("Organization ID", challenge.inspectedOrgId || "");
    row.set("Cédula", challenge.inspectedUserId || "");
    row.set("Productor", challenge.inspectedUserName || "");
    row.set("Finca", challenge.inspectedOrgName || "");

    const allAnswers: any[] = [];
    challenge.inspectionsProgramsVersionsItems.forEach((item) => {
      if (item.value) {
        try {
          const parsedAnswers = JSON.parse(item.value);
          if (Array.isArray(parsedAnswers)) {
            allAnswers.push(...parsedAnswers);
          }
        } catch (_e) { /* ignore */ }
      }
    });

    const answerLookup = new Map<string, any[]>();
    allAnswers.forEach((answer) => {
      const key = answer.questionId;
      if (!answerLookup.has(key)) {
        answerLookup.set(key, []);
      }
      answerLookup.get(key)!.push(answer);
    });

    sortedDynamicHeaders.forEach((header) => {
      if (header.includes(":")) {
        const [_parentId, option, childId] = header.split(":");
        const childAnswers = answerLookup.get(childId) || [];
        const contextualAnswer = childAnswers.find(
          (a) => a.forEachContextValue === option,
        );
        row.set(header, contextualAnswer ? String(contextualAnswer.value) : "");
      } else {
        const answers = answerLookup.get(header) || [];
        const values = answers.map((a) => a.value);
        row.set(header, values.join(", "));
      }
    });

    return finalHeaders.map((h) => row.get(h) || "");
  });

  const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
  const csvContent = [
    finalHeaders.map(escapeCsv).join(","),
    ...csvRows.map((r) => r.map(escapeCsv).join(",")),
  ].join("\n");

  return csvContent;
};

export const buildOptionsCsvContent = (
  data: ChallengesDetailedTableExportData,
): string => {
  const { programVersionItems } = data;

  const headers = [
    "Question ID",
    "Question Text",
    "Option Value",
    "Option Label",
  ];
  const rows: string[][] = [];

  programVersionItems.forEach((pvi) => {
    if (pvi.measurementTemplate) {
      try {
        const formDef = JSON.parse(pvi.measurementTemplate) as Form;
        if (formDef && Array.isArray(formDef.questions)) {
          formDef.questions.forEach((question) => {
            if (
              (question.type === "select" || question.type === "multiselect") &&
              question.options
            ) {
              question.options.forEach((option) => {
                rows.push([
                  question.id,
                  question.text,
                  option.value,
                  option.label,
                ]);
              });
            }
          });
        }
      } catch (e) {
        console.error(
          `Error parsing measurementTemplate for PVI ${pvi.id}:`,
          e,
        );
      }
    }
  });

  const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map(escapeCsv).join(",")),
  ].join("\n");

  return csvContent;
};

export const downloadCsvFile = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
